'use client';

import { Children, forwardRef, isValidElement, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { GleamRootContext } from './context/gleam-root.context';
import { gleamRootStyles } from './styles/gleam-root.styles';
import type { GleamOffsetInterface, GleamRootPropsInterface } from './types/gleam-root.types';

/**
 * @since 10.0.0
 */

export const GleamRoot = forwardRef<HTMLSpanElement, GleamRootPropsInterface>(
  (props, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;

    const hasMultipleChildren = useMemo(() => {
      const childrenArray = Children.toArray(children);
      return childrenArray.filter((child) => child !== undefined).length > 1;
    }, [children]);

    const floatingOffset = useMemo((): GleamOffsetInterface | undefined => {
      // Only compute specific offset if we have multiple children
      if (hasMultipleChildren) {
        const childrenArray = Children.toArray(children);
        const secondToLastChild = childrenArray.at(-2);

        if (isValidElement(secondToLastChild)) {
          const componentType = (secondToLastChild.type as { displayName?: string })?.displayName;
          const { size } = secondToLastChild.props;

          if (componentType === 'Avatar' || componentType === 'Icon') {
            const DEFAULT_COMPONENT_SIZES = {
              Avatar: 'lg',
              Icon: 'sm',
            };

            const resolvedSize = size || DEFAULT_COMPONENT_SIZES[componentType];

            return {
              component: componentType,
              size: resolvedSize,
            };
          }
        }
      }
      return undefined;
    }, [children, hasMultipleChildren]);

    const styles = css(gleamRootStyles, cssProp);

    const contextValue = useMemo(
      () => ({ hasMultipleChildren, floatingOffset }),
      [hasMultipleChildren, floatingOffset],
    );

    return (
      <GleamRootContext.Provider value={contextValue}>
        <span
          ref={forwardedRef}
          data-c11n-component="Gleam.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          {...rest}
        >
          {children}
        </span>
      </GleamRootContext.Provider>
    );
  },
);

GleamRoot.displayName = 'Gleam.Root';
